import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageHeader } from "@/components/AppShell";
import {
  pharmacyAdminBookingsApi,
  type AvailabilityWindow,
  type BlockoutDate,
} from "@/lib/callables";
import { useToast } from "@/lib/toast";

export default function RoomsPage() {
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string | null>(null);

  const pharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies"],
    queryFn: () => pharmacyAdminBookingsApi.listBookingPharmacies(),
  });

  const pharmacies = pharmaciesQ.data?.items ?? [];
  const hasAmbiguity = pharmacies.length > 1;

  // The callable returns records under the shared canonical rule, canonical
  // first (isPrimary). Duplicates are leftovers of the SAME pharmacy, not
  // distinct locations, so the canonical record is always the correct
  // default — it is the one the pharmacist's app writes availability to and
  // the one the patient booking link resolves to. Refusing to default (the
  // previous behaviour) left admins staring at an empty page, and letting
  // them pick a duplicate handed out a link with no availability behind it.
  const selectedPharmacy =
    pharmacies.find((p) => p.id === selectedPharmacyId) ?? pharmacies[0] ?? null;

  useEffect(() => {
    if (!selectedPharmacyId && pharmacies.length > 0) {
      setSelectedPharmacyId(pharmacies[0]!.id);
    }
  }, [pharmacies, selectedPharmacyId]);

  if (pharmaciesQ.isLoading) {
    return <Box sx={{ p: 4, textAlign: "center" }}>Loading...</Box>;
  }

  if (pharmacies.length === 0) {
    return (
      <>
        <PageHeader title="Rooms & Availability" />
        <Box sx={{ p: 4 }}>
          <Alert severity="info">
            No booking setup yet for this pharmacy. Ask a pharmacist to open the CCS Pharmacy app and complete the Bookings setup from their device — it will appear here automatically.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Rooms & Availability"
        description="Manage rooms and pharmacist availability."
        actions={
          hasAmbiguity && (
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Pharmacy Location</InputLabel>
              <Select
                value={selectedPharmacyId || ""}
                label="Pharmacy Location"
                displayEmpty
                onChange={(e) => setSelectedPharmacyId(e.target.value)}
              >
                {pharmacies.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                    {p.isPrimary ? " · Primary" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }
      />
      {hasAmbiguity && (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
          <Alert severity="warning">
            More than one booking record exists for this pharmacy. The record marked
            "Primary" is showing — it is the one the patient booking link and the mobile
            app's availability writes both resolve to. The others are leftover
            duplicates and should be merged by support (booking-record reconciliation);
            a link generated from one of them will show no availability.
          </Alert>
        </Box>
      )}
      {selectedPharmacy && (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <RoomsTab bookingPharmacyId={selectedPharmacy.id} />
        </Box>
      )}
    </>
  );
}

// ── Rooms & Availability ────────────────────────────────────────────────

function RoomsTab({ bookingPharmacyId }: { bookingPharmacyId: string }) {
  const [editingPharmacistId, setEditingPharmacistId] = useState<string | null>(null);

  const roomsQ = useQuery({
    queryKey: ["bookings", "rooms", bookingPharmacyId],
    queryFn: () => pharmacyAdminBookingsApi.listRooms({ bookingPharmacyId }),
  });

  const pharmacistsQ = useQuery({
    queryKey: ["bookings", "availability-pharmacists", bookingPharmacyId],
    queryFn: () => pharmacyAdminBookingsApi.listAvailabilityPharmacists({ bookingPharmacyId }),
  });

  const rooms = roomsQ.data?.items ?? [];
  const pharmacists = pharmacistsQ.data?.items ?? [];
  const editingPharmacist = pharmacists.find(p => p.pharmacistId === editingPharmacistId);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Rooms
        </Typography>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Rooms are created and managed by pharmacists from the CCS Pharmacy mobile app.
        </Alert>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id}>
                  <TableCell>{room.name}</TableCell>
                  <TableCell>{room.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={room.isActive ? "Active" : "Inactive"}
                      color={room.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {rooms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No rooms defined yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Pharmacist Availability
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Pharmacist</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pharmacists.map((p) => (
                <TableRow key={p.pharmacistId}>
                  <TableCell>{p.displayName}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditingPharmacistId(p.pharmacistId)}
                    >
                      Manage Availability
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pharmacists.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No pharmacist has set up their availability schedule yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {editingPharmacistId && (
        <Dialog
          open={!!editingPharmacistId}
          onClose={() => setEditingPharmacistId(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Manage Availability — {editingPharmacist?.displayName ?? editingPharmacistId}
          </DialogTitle>
          <AvailabilityEditForm
            key={editingPharmacistId}
            bookingPharmacyId={bookingPharmacyId}
            pharmacistId={editingPharmacistId}
            onClose={() => setEditingPharmacistId(null)}
          />
        </Dialog>
      )}
    </Stack>
  );
}

// ── Availability edit form (used inside Dialog) ───────────────────────────

const DAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

function AvailabilityEditForm({
  bookingPharmacyId,
  pharmacistId,
  onClose,
}: {
  bookingPharmacyId: string;
  pharmacistId: string;
  onClose: () => void;
}) {
  const { showError, showSuccess } = useToast();
  const qc = useQueryClient();

  const availQ = useQuery({
    queryKey: ["bookings", "availability", bookingPharmacyId, pharmacistId],
    queryFn: () => pharmacyAdminBookingsApi.getAvailability({ bookingPharmacyId, pharmacistId }),
  });

  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [blockoutDates, setBlockoutDates] = useState<BlockoutDate[]>([]);
  const [confirmRemoveWindow, setConfirmRemoveWindow] = useState<number | null>(null);
  const [confirmRemoveBlockout, setConfirmRemoveBlockout] = useState<number | null>(null);

  // Add-window form state
  const [newWinDay, setNewWinDay] = useState<number>(1);
  const [newWinStart, setNewWinStart] = useState("09:00");
  const [newWinEnd, setNewWinEnd] = useState("17:00");

  // Add-blockout form state
  const [newBODate, setNewBODate] = useState("");
  const [newBOReason, setNewBOReason] = useState("");

  // Re-sync local form state whenever the underlying query data changes
  // (initial load, refetch, or invalidation after save) instead of only
  // once — otherwise the form keeps showing whatever was fetched first.
  useEffect(() => {
    if (availQ.data === undefined) return;
    setWindows(availQ.data.availability?.windows ?? []);
    setBlockoutDates(availQ.data.availability?.blockoutDates ?? []);
  }, [availQ.data]);

  const saveM = useMutation({
    mutationFn: () =>
      pharmacyAdminBookingsApi.setAvailability({
        bookingPharmacyId,
        pharmacistId,
        windows,
        blockoutDates,
      }),
    onSuccess: () => {
      showSuccess("Availability saved");
      void qc.invalidateQueries({
        queryKey: ["bookings", "availability", bookingPharmacyId, pharmacistId],
      });
      onClose();
    },
    onError: (err) => showError(err instanceof Error ? err.message : "Failed to save availability"),
  });

  const addWindow = () => {
    if (!newWinStart || !newWinEnd) return;
    if (newWinEnd <= newWinStart) {
      showError("End time must be after start time");
      return;
    }
    setWindows((prev) => [
      ...prev,
      { dayOfWeek: newWinDay as AvailabilityWindow["dayOfWeek"], startTime: newWinStart, endTime: newWinEnd },
    ]);
  };

  const removeWindow = (i: number) => {
    setWindows((prev) => prev.filter((_, j) => j !== i));
    setConfirmRemoveWindow(null);
  };

  const addBlockout = () => {
    if (!newBODate) return;
    setBlockoutDates((prev) => [
      ...prev,
      { date: newBODate, ...(newBOReason ? { reason: newBOReason } : {}) },
    ]);
    setNewBODate("");
    setNewBOReason("");
  };

  const removeBlockout = (i: number) => {
    setBlockoutDates((prev) => prev.filter((_, j) => j !== i));
    setConfirmRemoveBlockout(null);
  };

  if (availQ.isLoading) {
    return (
      <>
        <DialogContent>
          <Typography color="text.secondary">Loading current schedule…</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    );
  }

  // A failed read must never render as "this pharmacist has no schedule": the
  // empty form below is also the editor, so saving from it would overwrite a
  // real week with nothing. Report the failure and offer a retry instead.
  if (availQ.isError) {
    return (
      <>
        <DialogContent>
          <Alert severity="error">
            {availQ.error instanceof Error
              ? availQ.error.message
              : "Could not load this pharmacist's schedule."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void availQ.refetch()}>Retry</Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={4}>
          {/* ── Weekly windows ─────────────────────────────── */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Weekly Windows
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {windows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="caption" color="text.secondary">
                          No recurring windows — add one below.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {windows.map((w, i) => (
                    <TableRow key={i}>
                      <TableCell>{DAY_NAMES[w.dayOfWeek]}</TableCell>
                      <TableCell>{w.startTime}</TableCell>
                      <TableCell>{w.endTime}</TableCell>
                      <TableCell align="right">
                        {confirmRemoveWindow === i ? (
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                            <Button size="small" variant="text" onClick={() => setConfirmRemoveWindow(null)}>
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => removeWindow(i)}
                            >
                              Remove
                            </Button>
                          </Stack>
                        ) : (
                          <IconButton size="small" onClick={() => setConfirmRemoveWindow(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  value={newWinDay}
                  label="Day"
                  onChange={(e) => setNewWinDay(Number(e.target.value))}
                >
                  {DAY_NAMES.map((d, i) => (
                    <MenuItem key={i} value={i}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Start"
                type="time"
                size="small"
                value={newWinStart}
                onChange={(e) => setNewWinStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 120 }}
              />
              <TextField
                label="End"
                type="time"
                size="small"
                value={newWinEnd}
                onChange={(e) => setNewWinEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 120 }}
              />
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addWindow}>
                Add Window
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* ── Blockout dates ─────────────────────────────── */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Blockout Dates
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockoutDates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="caption" color="text.secondary">
                          No blockout dates — add one below.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {blockoutDates.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell>{b.date}</TableCell>
                      <TableCell>{b.reason || "—"}</TableCell>
                      <TableCell align="right">
                        {confirmRemoveBlockout === i ? (
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                            <Button size="small" variant="text" onClick={() => setConfirmRemoveBlockout(null)}>
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => removeBlockout(i)}
                            >
                              Remove
                            </Button>
                          </Stack>
                        ) : (
                          <IconButton size="small" onClick={() => setConfirmRemoveBlockout(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={newBODate}
                onChange={(e) => setNewBODate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 160 }}
              />
              <TextField
                label="Reason (optional)"
                size="small"
                value={newBOReason}
                onChange={(e) => setNewBOReason(e.target.value)}
                sx={{ width: 220 }}
              />
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addBlockout}>
                Add Date
              </Button>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveM.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => saveM.mutate()}
          disabled={saveM.isPending}
        >
          {saveM.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </>
  );
}
